import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveMatch, type ActiveMatchDescriptor } from '@/store/activeMatch.slice';
import { useActiveMatchContext } from '@/contexts/ActiveMatchContext';

type StartDescriptor = Omit<ActiveMatchDescriptor, 'startedAt'>;

/**
 * Start a real-time match: register it as the one active match (so the provider
 * keeps its socket alive) and navigate to its screen. If a DIFFERENT match is
 * already live, confirm first — one match at a time; confirming resigns the old
 * one. `window.confirm` blocks the Telegram WebView briefly but is acceptable
 * here (rare, deliberate action); swap for an in-app sheet later if desired.
 */
export function useStartMatch() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('activeMatch');
  const current = useAppSelector((s) => s.activeMatch.current);
  const { resignActive } = useActiveMatchContext();

  return useCallback(
    (d: StartDescriptor) => {
      const path =
        d.game === 'qgammon'
          ? `/backgammon/match/${d.matchId}`
          : `/chinesepoker/match/${d.matchId}`;

      if (current && current.matchId !== d.matchId) {
        const ok = window.confirm(
          t('alreadyInMatch', {
            defaultValue: 'You already have a match in progress — abandon it?',
          }),
        );
        if (!ok) return;
        resignActive();
      }
      dispatch(setActiveMatch({ ...d, startedAt: Date.now() }));
      navigate(path);
    },
    [current, resignActive, dispatch, navigate, t],
  );
}
